/**
 * My plans (FD §3.1): plan cards with picture, name, last edited and by whom; open, rename,
 * duplicate, delete; a Deleted section to bring plans back within 30 days.
 */
import { useCallback, useEffect, useState } from 'react';
import * as api from '../../api';
import type { PlanMeta, TrashedPlanMeta } from '../../../shared/types';
import { ConfirmDialog, PromptDialog, errorText, toast } from './dialogs';
import { daysLeftInTrash, timeAgo } from './format';
import { Link, navigate } from './router';

type Dialog =
  | { kind: 'rename'; plan: PlanMeta }
  | { kind: 'delete'; plan: PlanMeta }
  | null;

/** The home page. */
export function MyPlans() {
  const [plans, setPlans] = useState<PlanMeta[] | null>(null);
  const [trash, setTrash] = useState<TrashedPlanMeta[]>([]);
  const [showTrash, setShowTrash] = useState(false);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState<Dialog>(null);

  const load = useCallback(async () => {
    try {
      const [p, t] = await Promise.all([api.listPlans(), api.listTrash()]);
      setPlans(p);
      setTrash(t);
      setError('');
    } catch (e) {
      setError(errorText(e));
    }
  }, []);

  useEffect(() => {
    void load();
    // Pick up changes made by the other person when coming back to this tab.
    const onFocus = () => document.visibilityState === 'visible' && void load();
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, [load]);

  const duplicate = async (plan: PlanMeta) => {
    try {
      await api.duplicatePlan(plan.id);
      toast(`Made a copy of "${plan.name}"`);
      await load();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const restore = async (plan: TrashedPlanMeta) => {
    try {
      await api.restoreFromTrash(plan.id);
      toast(`"${plan.name}" is back`);
      await load();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  return (
    <main className="page">
      <div className="page-head">
        <h1>My plans</h1>
        <button type="button" className="primary big" onClick={() => navigate('/new')}>
          + New plan
        </button>
      </div>

      {error && (
        <div className="banner warn">
          {error} <button type="button" onClick={() => void load()}>Try again</button>
        </div>
      )}

      {plans === null && !error && <p className="muted">Loading your plans…</p>}

      {plans?.length === 0 && (
        <div className="empty">
          <div className="empty-art" aria-hidden="true">🐘🦓🦜</div>
          <h2>No plans yet</h2>
          <p>Start your first zoo: pick some animals and the planner lays it out for you.</p>
          <button type="button" className="primary big" onClick={() => navigate('/new')}>Start a new plan</button>
        </div>
      )}

      {plans && plans.length > 0 && (
        <ul className="cards">
          {plans.map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              onRename={() => setDialog({ kind: 'rename', plan: p })}
              onDuplicate={() => void duplicate(p)}
              onDelete={() => setDialog({ kind: 'delete', plan: p })}
            />
          ))}
        </ul>
      )}

      {trash.length > 0 && (
        <section className="trash">
          <button type="button" className="disclosure" aria-expanded={showTrash} onClick={() => setShowTrash(!showTrash)}>
            {showTrash ? '▾' : '▸'} Deleted ({trash.length})
          </button>
          {showTrash && (
            <>
              <p className="hint">Deleted plans are kept for 30 days, then removed for good.</p>
              <ul className="trash-list">
                {trash.map((t) => {
                  const days = daysLeftInTrash(t.deletedAt);
                  return (
                    <li key={t.id}>
                      <div>
                        <strong>{t.name}</strong>
                        <span className="muted">
                          {' '}deleted {timeAgo(t.deletedAt)}{t.deletedBy ? ` by ${t.deletedBy}` : ''} ·{' '}
                          {days <= 1 ? 'removed for good tomorrow' : `removed for good in ${days} days`}
                        </span>
                      </div>
                      <button type="button" onClick={() => void restore(t)}>Bring back</button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </section>
      )}

      {dialog?.kind === 'rename' && (
        <PromptDialog
          title="Rename plan"
          label="Name"
          initial={dialog.plan.name}
          confirmLabel="Rename"
          onClose={() => setDialog(null)}
          onSubmit={async (name) => {
            await api.renamePlan(dialog.plan.id, name);
            await load();
          }}
        />
      )}
      {dialog?.kind === 'delete' && (
        <ConfirmDialog
          title="Delete plan?"
          confirmLabel="Delete"
          danger
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await api.deletePlan(dialog.plan.id);
            toast(`"${dialog.plan.name}" moved to Deleted`);
            await load();
          }}
        >
          <p>
            <strong>{dialog.plan.name}</strong> moves to <em>Deleted</em> at the bottom of this page. You can bring it back
            for 30 days.
          </p>
        </ConfirmDialog>
      )}
    </main>
  );
}

interface CardProps {
  plan: PlanMeta;
  onRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function PlanCard({ plan, onRename, onDuplicate, onDelete }: CardProps) {
  const [menu, setMenu] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menu]);
  const href = `/plan/${encodeURIComponent(plan.id)}`;
  return (
    <li className="card">
      <Link to={href} className="card-link" aria-label={`Open ${plan.name}`}>
        <div className="thumb">
          {plan.hasThumb && !thumbFailed ? (
            <img src={api.thumbUrl(plan.id, plan.rev)} alt="" loading="lazy" onError={() => setThumbFailed(true)} />
          ) : (
            <div className="thumb-empty" aria-hidden="true">🗺️</div>
          )}
        </div>
        <div className="card-text">
          <strong className="card-name">{plan.name}</strong>
          <span className="muted">
            Edited {timeAgo(plan.updatedAt)}{plan.updatedBy ? ` by ${plan.updatedBy}` : ''}
          </span>
        </div>
      </Link>
      <div className="card-menu">
        <button
          type="button"
          className="icon-btn"
          aria-label={`More for ${plan.name}`}
          aria-expanded={menu}
          onClick={(e) => {
            e.stopPropagation();
            setMenu(!menu);
          }}
        >
          ⋯
        </button>
        {menu && (
          <div className="menu" role="menu">
            <button type="button" role="menuitem" onClick={() => navigate(href)}>Open</button>
            <button type="button" role="menuitem" onClick={onRename}>Rename…</button>
            <button type="button" role="menuitem" onClick={onDuplicate}>Make a copy</button>
            <button type="button" role="menuitem" className="danger-text" onClick={onDelete}>Delete…</button>
          </div>
        )}
      </div>
    </li>
  );
}
